import { useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { Tree, TreeCheckboxSelectionKeys } from 'primereact/tree'
import { useStructureContext } from '../contexts/StructureContext'
import DialogContainer from '../common/pure/DialogContainer'
import { Column, Row } from '../common/pure/FlexContainer'
import {
    MAX_SERVICE_CONNECTIONS,
    selectedServiceBoltIds,
    serviceSelectionKeys,
    serviceSelectionNodes,
} from '../../tools/service-window'
import t from '../../tools/t'

export default function ServiceWindowConnectionsDialog({
    ids,
    onApply,
    onHide,
}: {
    ids: string[]
    onApply: (ids: string[]) => void
    onHide: () => void
}) {
    const { structure, structureLoading, error, refreshStructure } = useStructureContext()
    const nodes = useMemo(() => serviceSelectionNodes(structure.map((entry) => entry)), [structure])
    const [selectedIds, setSelectedIds] = useState(ids)
    const selectableIds = selectedServiceBoltIds(
        nodes,
        serviceSelectionKeys(
            nodes,
            structure
                .map((entry) => entry)
                .flatMap(function collect(entry): string[] {
                    return [entry.id, ...entry.children.flatMap(collect)]
                })
        )
    )
    const onSelectionChange = (keys: TreeCheckboxSelectionKeys) => {
        // Keep unavailable references until the user explicitly removes their chip in the form.
        const unavailable = selectedIds.filter((id) => !selectableIds.includes(id))
        setSelectedIds([...unavailable, ...selectedServiceBoltIds(nodes, keys)])
    }
    return (
        <DialogContainer
            visible
            modal
            size="medium"
            header={t('service_window_select_connections')}
            onHide={onHide}
            footer={
                <Row gap={0.5} arrangement="end">
                    <Button outlined label={t('common_cancel')} onClick={onHide} />
                    <Button
                        label={t('service_window_apply_selection')}
                        disabled={
                            structureLoading ||
                            !!error ||
                            selectedIds.length > MAX_SERVICE_CONNECTIONS
                        }
                        onClick={() => {
                            onApply(selectedIds)
                            onHide()
                        }}
                    />
                </Row>
            }
        >
            <Column gap={1}>
                <p>{t('service_window_folder_help')}</p>
                {error ? (
                    <>
                        <Message severity="error" text={error.message} />
                        <Button label={t('service_window_retry')} onClick={refreshStructure} />
                    </>
                ) : (
                    <Tree
                        value={nodes}
                        loading={structureLoading}
                        selectionMode="checkbox"
                        filter
                        filterMode="lenient"
                        filterPlaceholder={t('service_window_search_connections')}
                        selectionKeys={serviceSelectionKeys(nodes, selectedIds)}
                        onSelectionChange={(event) =>
                            onSelectionChange(event.value as TreeCheckboxSelectionKeys)
                        }
                        emptyMessage={t('service_window_no_connections')}
                    />
                )}
                <span>
                    {t(
                        'service_window_selection_count',
                        String(selectedIds.length),
                        String(MAX_SERVICE_CONNECTIONS)
                    )}
                </span>
                {selectedIds.length > MAX_SERVICE_CONNECTIONS && (
                    <Message severity="error" text={t('service_window_too_many')} />
                )}
            </Column>
        </DialogContainer>
    )
}
